<script>
  // The shared body of the Ready, Solved and Error pages.
  //
  // Those three were ~65 lines each and differed in exactly seven things: the
  // endpoint, the state variable's name, the card component, the header
  // colour, the title, the empty message, and one extra prop on the Solved and
  // Error cards. Everything else -- the fetch, the loading and error states,
  // the tab strip, the card wrapper -- was identical. They are now thin
  // wrappers that pass their differences in as props and render their own card
  // through the default slot.
  //
  // The Queue page deliberately does NOT use this: it is socket-driven, shows
  // two lists rather than one, and has no fetch at all.

  import { onMount } from "svelte";
  import { apiFetch } from "../../lib/api.js";
  import { refreshCounts } from "../../lib/counts.js";
  import TabBar from "../TabBar.svelte";
  import PageSection from "../UI/PageSection.svelte";
  import EmptyState from "../UI/EmptyState.svelte";
  import LoadingState from "../UI/LoadingState.svelte";

  /** API endpoint returning {success, files} for this page's status. */
  export let url;
  /** Heading shown above the results. */
  export let title;
  /** Shown when the server returned no jobs. */
  export let emptyMessage;
  export let emptyHint = "";
  export let emptyIcon = "fa-inbox";

  let files = [];
  let loading = true;
  let loadError = "";

  onMount(load);

  async function load() {
    try {
      const result = await apiFetch(url, { method: "POST" });
      files = result.files;
      loadError = "";
    } catch (err) {
      // `loading` used to be cleared only inside the success branch, so any
      // failure left a permanent spinner with no explanation.
      loadError = err.message;
    } finally {
      loading = false;
    }
  }

  /**
   * Refetches in place. Exposed to the cards through the default slot, so a
   * delete refreshes the list without window.location.reload() throwing away
   * the tab and the scroll position.
   */
  export async function refresh() {
    // The tab badges are counted server-side, so they have to be re-asked --
    // otherwise a delete leaves the heading saying "5 jobs" under a tab still
    // claiming 6.
    await Promise.all([load(), refreshCounts()]);
  }
</script>

<div class="flex flex-col gap-4">
  <slot name="top" />

  <TabBar />

  {#if loading}
    <LoadingState />
  {:else if loadError}
    <EmptyState
      icon="fa-exclamation-triangle"
      title="Could not load jobs"
      hint={loadError}
    >
      <button
        class="btn btn-sm btn-primary mt-2 normal-case"
        on:click={refresh}
      >
        Try again
      </button>
    </EmptyState>
  {:else if files.length === 0}
    <EmptyState icon={emptyIcon} title={emptyMessage} hint={emptyHint} />
  {:else}
    <PageSection {title} count={files.length}>
      {#each files as file (file.id)}
        <slot {file} {refresh} />
      {/each}
    </PageSection>
  {/if}
</div>
